<template>
  <v-container>
    <!-- Split Header Layout -->
    <v-row class="mb-6 align-center px-4">
      <div class="d-flex align-center py-4">
        <div>
          <div class="text-h4 font-weight-black primary--text d-flex align-center">
            Materiales
          </div>
          <div class="caption grey--text font-weight-medium">Gestión de insumos y costos de producción</div>
        </div>
      </div>
      <v-spacer></v-spacer>
      <div class="d-flex align-center">
        <v-btn outlined color="primary" depressed @click="$router.push({name:'ImportSupplies',params:{data:{units:units,groups:groups,supplies:costs}}})" class="mr-2 shadow-sm">
          <v-icon left small>fas fa-file-import</v-icon> Importar
        </v-btn>
        <v-btn color="primary" depressed @click="create" class="px-6 shadow-sm">
          <v-icon left small>fas fa-plus</v-icon> Nuevo Material
        </v-btn>
      </div>
    </v-row>

    <!-- Filters -->
    <v-card outlined class="modern-card pa-6 mb-6">
      <v-row dense align="center">
        <v-col cols="12" md="4">
          <v-select
            v-model="groupFilter"
            :items="groups"
            item-text="name"
            item-value="id"
            label="Filtrar por Grupo"
            placeholder="Todos los Grupos"
            outlined
            dense
            clearable
            hide-details="auto"
            class="modern-select"
          />
        </v-col>
        <v-spacer></v-spacer>
        <v-col cols="12" md="6">
          <v-text-field
            v-model="search"
            @keyup="searchTimeOut"
            label="Búsqueda de materiales"
            placeholder="Nombre o descripción..."
            prepend-inner-icon="fas fa-search"
            outlined
            dense
            hide-details="auto"
            clearable
            :disabled="loading"
            class="modern-search-field"
          ></v-text-field>
        </v-col>
      </v-row>
    </v-card>

    <!-- Data Table -->
    <v-card outlined class="modern-card elevation-0 overflow-hidden">
      <v-data-table
        :headers="headers"
        :items="costs"
        :loading="loading"
        :options.sync="options"
        :server-items-length="totalItems"
        class="modern-table"
        :footer-props="{
          showFirstLastPage: true,
          itemsPerPageOptions: [10, 20, 50, 100],
        }"
        @update:options="fetch"
      >
        <template v-slot:item.id="{ item }">
          <span class="caption grey--text font-weight-bold">#{{ item.id }}</span>
        </template>

        <template v-slot:item.name="{ item }">
          <div class="d-flex align-center py-2">
            <span class="body-2 font-weight-bold primary--text">{{ item.name }}</span>
            <v-tooltip top v-if="item.has_dimension">
              <template v-slot:activator="{ on, attrs }">
                <v-icon class="ml-2 grey--text" x-small v-bind="attrs" v-on="on">fas fa-ruler-combined</v-icon>
              </template>
              <span>Dimensión: {{ item.width }}x{{ item.height }}</span>
            </v-tooltip>
            <v-tooltip top v-if="item.has_article">
              <template v-slot:activator="{ on, attrs }">
                <v-icon class="ml-2 grey--text" x-small v-bind="attrs" v-on="on">fas fa-link</v-icon>
              </template>
              <span>Vinculado a: {{ item.article.sku }}</span>
            </v-tooltip>
          </div>
        </template>

        <template v-slot:item.price="{ item }">
          <span class="body-2 font-weight-black success--text">{{ item.price | moneda }}</span>
        </template>

        <template v-slot:item.unit_name="{ item }">
          <v-chip outlined x-small label color="grey lighten-1" class="font-weight-bold">{{ item.unit_name }}</v-chip>
        </template>

        <template v-slot:item.is_active="{ item }">
          <v-switch 
            v-model="item.is_active" 
            @change="updateStatus(item)" 
            :disabled="!$can('update','Cost')"
            dense 
            inset 
            hide-details 
            color="success"
            class="mt-0"
          ></v-switch>
        </template>

        <template v-slot:item.actions="{ item }">
          <div class="d-flex justify-center">
            <v-btn icon small color="primary" @click.stop="edit(item)">
              <v-icon small>fas fa-edit</v-icon>
            </v-btn>
            <v-btn icon small color="grey darken-1" @click.stop="showPriceLog(item)">
              <v-icon small>fas fa-history</v-icon>
            </v-btn>
          </div>
        </template>
      </v-data-table>
    </v-card>

    <!-- Modals -->
    <v-dialog v-model="showDialog" max-width="600px" persistent>
      <cost-create-update 
        :cost="newCost" 
        :groups="groups" 
        @save="save"
        @close="showDialog = false"
      /> 
    </v-dialog>

    <v-dialog v-model="showPriceLogDialog" max-width="800px">
      <v-card outlined class="modern-card overflow-hidden">
        <v-sheet color="grey lighten-4" class="px-6 py-4 border-bottom">
          <div class="d-flex align-center">
            <v-icon color="primary" class="mr-3">fas fa-history</v-icon>
            <div>
              <div class="text-h6 font-weight-bold lh-1">Historial de Precios</div>
              <div class="caption grey--text mt-1">{{ selectedCost.name }}</div>
            </div>
          </div>
        </v-sheet>
        <v-card-text class="pa-0">
          <price-log-table :cost="selectedCost" @close="showPriceLogDialog = false" />
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import {fetchCost,createCost, updateCost,fetchGroups,fetchUnits} from '@/api/products'
import notifications from '@/utils/notifications'
import CostCreateUpdate from '@/components/products/costs/CostCreateUpdate'
import PriceLogTable from '@/components/products/costs/PriceLogTable'

export default {
  name: 'Supplies',
  components: { CostCreateUpdate, PriceLogTable },
  data: () => ({
    is_supply: true,
    costs: [],
    search: '',
    timer: '',
    showDialog: false,
    loading: false,
    groups: [],
    groupFilter: '',
    options: {},
    totalItems: 0,
    units: [],
    showPriceLogDialog: false,
    selectedCost: '',
    newCost: { id: -1 },
    headers: [
      { text: 'ID', value: 'id', align: 'start', width: '80', class: 'grey lighten-4' },
      { text: 'Descripción Material', value: 'name', align: 'start', class: 'grey lighten-4' },
      { text: 'Valor / Costo', value: 'price', align: 'end', width: '120', class: 'grey lighten-4' },
      { text: 'Unidad', value: 'unit_name', align: 'center', width: '100', class: 'grey lighten-4' },
      { text: 'Grupo', value: 'group_name', align: 'start', class: 'grey lighten-4' },
      { text: 'Estatus', value: 'is_active', align: 'center', width: '90', class: 'grey lighten-4' },
      { text: 'Acciones', value: 'actions', align: 'center', width: '110', class: 'grey lighten-4', sortable: false }
    ],
  }),
  methods: {
    edit(item) {
      this.newCost = { ...item }
      this.showDialog = true
    },
    showPriceLog(item) {
      this.selectedCost = item
      this.showPriceLogDialog = true
    },
    create() {
      this.newCost = {
        id: -1,
        group: '',
        unit: '',
        name: '',
        price: 0,
        is_supply: this.is_supply,
        has_dimension: false,
        width: 0,
        height: 0,
        has_article: false,
        price_update_option: 0,
      }
      this.showDialog = true
    },
    async save(cost) {
      this.loading = true
      try {
        if (cost.id === -1) {
          await createCost({ ...cost, is_supply: this.is_supply })
          notifications.showSuccess('Material creado correctamente')
        } else {
          await updateCost(cost)
          notifications.showSuccess('Material actualizado correctamente')
        }
        this.showDialog = false
        this.fetch()
      } catch (err) {
        notifications.showError('Error al guardar: ' + err)
      } finally {
        this.loading = false
      }
    },
    async updateStatus(item) {
      try {
        await updateCost({ ...item })
        notifications.showSuccess('Estado actualizado')
      } catch (e) {
        notifications.showError('Error: ' + e)
        item.is_active = !item.is_active
      }
    },
    async fetch() {
      this.loading = true
      const { sortBy, sortDesc, page, itemsPerPage } = this.options
      let sort = ''
      if (sortBy && sortBy[0]) {
        if (sortBy[0] == 'group_name') sort = 'group__name'
        else if (sortBy[0] == 'unit_name') sort = 'unit__name'
        else sort = sortBy[0]
      }
      
      const query = `search=${this.search || ''}&ordering=${sortDesc && sortDesc[0] ? sort : '-' + sort}&page=${page || 1}&page_size=${itemsPerPage || 20}&is_supply=${this.is_supply}&group=${this.groupFilter || ''}`
      try {
        const data = await fetchCost(query)
        this.totalItems = data.count
        this.costs = data.results
      } catch (err) {
        notifications.showError('Error en carga: ' + err)
      } finally {
        this.loading = false
      }
    },
    searchTimeOut() {
      if (this.timer) clearTimeout(this.timer);
      this.timer = setTimeout(() => {
        this.fetch()
      }, 800);
    },
  },
  async mounted() {
    this.groups = await fetchGroups(this.is_supply)
    const res = await fetchUnits()
    this.units = res
    this.fetch()
  }
}
</script>

<style scoped>
.modern-card {
  border: 1px solid rgba(0,0,0,0.08) !important;
  border-radius: 12px !important;
}
.shadow-sm { box-shadow: 0 2px 4px rgba(0,0,0,0.05) !important; }
.border-bottom { border-bottom: 1px solid rgba(0,0,0,0.08); }
.lh-1 { line-height: 1.2; }
</style>